import React from 'react';
import Button, { CustomThemeButton } from '@atlaskit/button';
import { LoomLogo } from '@atlaskit/logo';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { getTheme } from '@atlassian/jira-common-theme';
import { useIntl } from '@atlassian/jira-intl';
import { type UIAnalyticsEvent, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { ModalDialog } from '@atlassian/jira-software-modal-dialog';
import { SIGN_UP_URL, STYLES, SUBJECT_IDS, TEST_IDS } from '../constants';
import { useIsDarkMode } from '../hooks/use-is-dark-mode';
import messages from '../messages';
import { LoomPreview } from './preview';
import { ProductFeatureList } from './product-feature-list';

export type Props = {
	isModalOpen: boolean;
	onClose: () => void;
};

const LoomAdModalView = ({ isModalOpen, onClose }: Props) => {
	const { formatMessage } = useIntl();

	const isDarkMode = useIsDarkMode();

	const handleCancelClick = (
		_: React.MouseEvent<HTMLElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		fireUIAnalytics(analyticsEvent, SUBJECT_IDS.modalCancelButton);
		onClose();
	};

	const handleCtaClick = (
		_: React.MouseEvent<HTMLElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		fireUIAnalytics(analyticsEvent, SUBJECT_IDS.modalCtaButton);
	};

	const customTheme = getTheme({
		buttonStyles: {
			primary: {
				backgroundColor: isDarkMode ? STYLES.modal.colors.white : STYLES.modal.colors.purple,
				borderRadius: token('space.250', '20px'),
				color: token('color.text.inverse', '#FFFFFF'),
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				paddingLeft: token('space.150', '12px'),
				paddingRight: token('space.150', '12px'),
				marginLeft: token('space.050', '4px'),
				textAlign: 'center',
			},
		},
	});

	return (
		<ModalTransition>
			{isModalOpen && (
				<ModalDialog onClose={onClose} height={640} width={1140}>
					<Flex
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						xcss={xcss({
							height: '100%',
							width: '100%',
							borderRadius: 'border.radius.100',
							overflow: 'auto',
							backgroundColor: 'elevation.surface',
						})}
					>
						<Flex
							direction="column"
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
							xcss={xcss({
								height: '100%',
								width: '570px',
								boxSizing: 'border-box',
								padding: 'space.800',
							})}
						>
							<LoomLogo appearance="brand" />
							<Box
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
								xcss={xcss({
									marginTop: 'space.500',
									fontSize: '29px',
									fontWeight: '400',
								})}
							>
								{formatMessage(messages.headingNoTranslate)}
							</Box>
							<Box
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
								xcss={xcss({
									marginTop: 'space.100',
									flexGrow: '1',
								})}
							>
								<ProductFeatureList />
							</Box>
							<Flex
								justifyContent="end"
								gap="space.025"
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
								xcss={xcss({
									marginTop: 'space.500',
								})}
							>
								<Button
									onClick={handleCancelClick}
									appearance="subtle"
									testId={TEST_IDS.cancelSignupButton}
								>
									{formatMessage(messages.cancelNoTranslate)}
								</Button>
								<CustomThemeButton
									// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
									theme={customTheme}
									target="_blank"
									href={SIGN_UP_URL}
									appearance="primary"
									onClick={handleCtaClick}
									testId={TEST_IDS.getStartedLink}
								>
									{formatMessage(messages.getStartedNoTranslate)}
								</CustomThemeButton>
							</Flex>
						</Flex>
						<LoomPreview />
					</Flex>
				</ModalDialog>
			)}
		</ModalTransition>
	);
};

export default LoomAdModalView;
